import {
  useGlobalNotifier,
  useMessage,
  useSpreadsheetState,
} from "@vrf/fr-vrf-core-react";
import { FrVrfSpreadsheet } from "@vrf/fr-vrf-spreadsheet-react";
import {
  FrVrfButton,
  FrVrfButtonPanel,
  FrVrfFooter,
  FrVrfFormPanel,
  FrVrfRadioButtonField,
} from "@vrf/fr-vrf-web-components-react";
import React, { useEffect, useMemo } from "react";
import {
  CellItem,
  InputComponentRequirementDialogInitialState,
  Option,
  SpreadsheetRecordData,
  SpreadsheetState,
  useInputComponentRequirementDialogState,
  displayColumn,
  DisplayColumnKeys,
} from ".";
import { ApplicationMessageScreen } from "../../../messages/screen-message";
import { usePageNavigation } from "../../../pages/use-page-navigation";
import {
  useGetDipByRegionList,
  useGetGeneralCodes,
} from "../../../services/data-list";
import { useUpdateProject } from "../../../services/project";
import { mappingErrorResponseToSpreadsheet } from "../../../util/spreadsheet-data-utils";
import { createModal } from "../../modal";
import _ from "lodash";
import { Cell } from "@vrf/fr-vrf-spreadsheet-core-templating";

enum ReqType {
  OnlyIf = "OnlyIf",
  Both = "Both",
}

const MANUAL_LINK = {
  tooltip: "3. Confirm Requirements and Estimate",
  value:
    "https://confluence.fastretailing.com/pages/viewpage.action?pageId=817541348",
};

const TEMPLATE_FILE_URL = `spreadsheet-template/project-plan-component-requirement-list.xlsx`;

export const InputComponentRequirementDialogContent: React.FC = () => {
  const { showToast, showConfirmationDialog, errorMessages, resetUserError } =
    useGlobalNotifier();
  const { messageForScreen } = useMessage();
  const [getGeneralCodesResponse, getGeneralCodesRequest] =
    useGetGeneralCodes();
  const [getDipByRegionListResponse, getDipByRegionListRequest] =
    useGetDipByRegionList();
  const [updateProjectResponse, updateProjectRequest] = useUpdateProject();
  const [pageState, setPageState] = useInputComponentRequirementDialogState();
  const [spreadsheetState, setSpreadsheetState, initSpreadsheetState] =
    useSpreadsheetState<SpreadsheetState>();
  const { moveToModalPage, closeModalPage, isModalPage } = usePageNavigation();

  const closeModal = (isSaved: boolean) => {
    if (!isSaved) {
      resetUserError();
      setPageState(InputComponentRequirementDialogInitialState);
      return;
    }
    closeModalPage();

    setPageState((prevState) => ({
      ...prevState,
      isShow: false,
      status: {
        ...prevState.status,
        isSaved: true,
      },
    }));
  };

  const onInitialDisplayed = (): void => {
    moveToModalPage();
    resetUserError();
    const cdTypes = ["tst_use_flg"];
    getGeneralCodesRequest({ cdTypes: cdTypes });
    getDipByRegionListRequest({});
  };
  useEffect(onInitialDisplayed, []);

  const ongetGetGeneralCodes = () => {
    let tstUseFlg = [] as Option[];
    const generalCode = getGeneralCodesResponse?.gnrlCdTypeList ?? [];

    generalCode.forEach((e) => {
      switch (e.cdType) {
        case "tst_use_flg":
          tstUseFlg = e.cdList.map((e) => {
            return {
              label: e.label,
              value: e.value,
            };
          });
          break;
      }
    });

    setPageState((prevState) => ({
      ...prevState,
      codes: {
        ...prevState.codes,
        tstUseFlg: tstUseFlg,
      },
    }));
  };
  useEffect(ongetGetGeneralCodes, [getGeneralCodesResponse]);

  const onGetDipByRegionListResponse = () => {
    if (!getDipByRegionListResponse) return;

    setPageState((prevState) => ({
      ...prevState,
      codes: {
        ...prevState.codes,
        dipByRegionList: getDipByRegionListResponse.dipByRgnList.map((e) => ({
          label: e.dipByRgn,
          value: e.dipByRgn,
        })),
      },
    }));
  };
  useEffect(onGetDipByRegionListResponse, [getDipByRegionListResponse]);

  const spreadsheetInitialData = useMemo(() => {
    if (pageState.edited.componentRequirementList === undefined) return;

    const spreadsheetRecordData: SpreadsheetRecordData[] =
      pageState.edited.componentRequirementList.map((e) => {
        return {
          ...InputComponentRequirementDialogInitialState.spreadsheet.tmplateRow,
          ...e,
        };
      });

    if (spreadsheetRecordData.length === 0) {
      spreadsheetRecordData.push({
        ...InputComponentRequirementDialogInitialState.spreadsheet.tmplateRow,
        isAddRow: true,
      });
    }

    return {
      results: spreadsheetRecordData,
      codes: {
        dipByRegionList: pageState.codes.dipByRegionList,
      },
      templateItem:
        InputComponentRequirementDialogInitialState.spreadsheet.tmplateRow,
    };
  }, [
    pageState.edited.reqType,
    pageState.edited.componentRequirementList,
    pageState.codes.dipByRegionList,
  ]);

  const onChangeValueHandle = (name: string) => (event: CustomEvent) => {
    const value = event.detail.value;
    const isValid = event.detail.isValid;

    if (name === "reqType") {
      if (
        value === ReqType.OnlyIf &&
        pageState.saved.componentRequirementList !== undefined &&
        pageState.saved.componentRequirementList.length > 0
      ) {
        const newSpreadsheetRecordData: SpreadsheetRecordData[] =
          spreadsheetState
            ? spreadsheetState.results.map((e) => {
                return {
                  ...InputComponentRequirementDialogInitialState.spreadsheet
                    .tmplateRow,
                  ...e,
                  delFlg: true,
                };
              })
            : [];
        setSpreadsheetState({
          ...spreadsheetState,
          results: [...newSpreadsheetRecordData],
        });
      } else if (
        value === ReqType.OnlyIf &&
        spreadsheetState !== undefined &&
        spreadsheetState.results.length > 0
      ) {
        setSpreadsheetState({
          ...spreadsheetState,
          results: [],
        });
      }

      setPageState((prevState) => ({
        ...prevState,
        edited: {
          ...prevState.edited,
          reqType: value,
        },
        status: {
          ...prevState.status,
          invalidList:
            value === ReqType.OnlyIf
              ? prevState.status.invalidList.filter(
                  (e) => e !== "componentRequirementList",
                )
              : prevState.status.invalidList,
          invalidRecordList:
            value === ReqType.OnlyIf ? [] : prevState.status.invalidRecordList,
        },
      }));
    } else {
      setPageState((prevState) => ({
        ...prevState,
        edited: {
          ...prevState.edited,
          [name]: value,
        },
        status: {
          ...prevState.status,
          invalidList: isValid
            ? prevState.status.invalidList.filter((e) => e !== name)
            : prevState.status.invalidList.concat(name),
        },
      }));
    }
  };

  const onCellEditedHandle = (event: CustomEvent) => {
    const rowIdx: number = event.detail.cell.data.index;
    const value: string = event.detail.cell.value ?? "";
    const isChangedRow: boolean = event.detail.cell._row.isChanged;
    const name = event.detail.name;
    const selectedRow = event.detail.value;
    const isValid = event.detail.cell.valid;
    const cellItem: CellItem = {
      rowIdx: rowIdx,
      col: name,
    };

    const newSpreadsheetRecordData: SpreadsheetRecordData[] = spreadsheetState
      ? [...spreadsheetState.results]
      : [];

    switch (name) {
      case "delete":
        newSpreadsheetRecordData[rowIdx].delFlg = !selectedRow.delFlg;
        break;
      case "dipByRegion":
        newSpreadsheetRecordData[rowIdx].dipByRgn = value;
        break;
      case "componentId":
        newSpreadsheetRecordData[rowIdx].cpntId = value;
        break;
      case "description":
        newSpreadsheetRecordData[rowIdx].cpntReqDesc = value;
        break;
    }

    newSpreadsheetRecordData[rowIdx].updateFlag = isChangedRow;
    setSpreadsheetState({
      ...spreadsheetState,
      results: [...newSpreadsheetRecordData],
    });

    setPageState((prevState) => ({
      ...prevState,
      status: {
        ...prevState.status,
        invalidRecordList: isValid
          ? prevState.status.invalidRecordList.filter(
              (e) => e !== JSON.stringify(cellItem),
            )
          : prevState.status.invalidRecordList.concat(JSON.stringify(cellItem)),
      },
    }));
  };

  const onCellBulkEdited = (event: CustomEvent) => {
    if (!spreadsheetState) return;
    const newComponentRequirementList = _.cloneDeep(
      spreadsheetState?.results,
    ) as SpreadsheetRecordData[];

    const colName = event.detail.name as DisplayColumnKeys;
    event.detail.affectedCells.cells.forEach((item: Cell) => {
      const value: string = item.value;
      const rowIdx: number = item.data.index;

      const isChangedRow: boolean = item.row.isChanged;
      if (colName === "delFlg") {
        newComponentRequirementList[rowIdx].delFlg = value === "true";
      } else newComponentRequirementList[rowIdx][colName] = value;
      newComponentRequirementList[rowIdx].updateFlag = isChangedRow;
    });

    setSpreadsheetState({
      ...spreadsheetState,
      results: [...newComponentRequirementList],
    });
  };

  const onCellClickedHandle = (event: CustomEvent) => {
    const rowIdx: number = event.detail.cell.data.index;
    const name = event.detail.name;

    if (name !== "deleteButton") return;
    const newSpreadsheetRecordData: SpreadsheetRecordData[] = spreadsheetState
      ? [...spreadsheetState.results]
      : [];

    if (newSpreadsheetRecordData.length <= 1) {
      newSpreadsheetRecordData[0] = {
        ...InputComponentRequirementDialogInitialState.spreadsheet.tmplateRow,
      };
    } else {
      newSpreadsheetRecordData.splice(rowIdx, 1);
    }
    setSpreadsheetState({
      ...spreadsheetState,
      results: [...newSpreadsheetRecordData],
    });
  };

  const onCellSelectedHandle = (event: CustomEvent) => {
    const rowIndex: number = event.detail.cell?.data.index;
    setPageState((prevState) => ({
      ...prevState,
      spreadsheet: {
        ...prevState.spreadsheet,
        selectRowIdx: rowIndex,
      },
    }));
  };

  const onClickAddRow = () => {
    const insertRowIndex: number =
      (pageState.spreadsheet.selectRowIdx ?? -1) + 1;

    const newSpreadsheetRecordData: SpreadsheetRecordData[] = spreadsheetState
      ? [...spreadsheetState.results]
      : [];

    const addSpreadsheetRow: SpreadsheetRecordData = {
      ...InputComponentRequirementDialogInitialState.spreadsheet.tmplateRow,
      isAddRow: true,
    };

    newSpreadsheetRecordData.splice(insertRowIndex, 0, addSpreadsheetRow);
    setSpreadsheetState({
      ...spreadsheetState,
      results: [...newSpreadsheetRecordData],
    });
  };

  const onClickCancel = () => {
    if (
      pageState.edited.reqType === "Both"
        ? JSON.stringify(pageState.saved) ===
            JSON.stringify(pageState.edited) &&
          !spreadsheetState?.results.some((e) => e.updateFlag)
        : JSON.stringify(pageState.saved) === JSON.stringify(pageState.edited)
    ) {
      closeModal(false);
    } else {
      showConfirmationDialog({
        title: "",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00033),
        type: "confirm",
        onOkButtonClicked: () => {
          closeModal(false);
        },
      });
    }
  };

  const onClickSave = () => {
    const updateProject = () => {
      resetUserError();
      let componentRequirementList = spreadsheetState?.results.map((e, idx) => {
        return {
          cpntReqId: e.cpntReqId ?? "",
          dipByRgn: e.dipByRgn,
          cpntId: e.cpntId,
          cpntReqDesc: e.cpntReqDesc,
          delFlg: e.delFlg,
          index: idx,
        };
      });

      if (componentRequirementList === undefined) componentRequirementList = [];

      const request = {
        pjtId: pageState.pjtId,
        tstUseTypeCd: pageState.edited.tstUseTypeCd,
        componentRequirementList: componentRequirementList,
        updateDateTime: pageState.updateDateTime,
      };

      updateProjectRequest(request);
    };

    showConfirmationDialog({
      title: "",
      message: "",
      type: "confirm",
      onOkButtonClicked: updateProject,
    });
  };

  const onListenUpdateProjectResponse = (): void => {
    if (!updateProjectResponse) return;

    console.log("updateProjectResponse");
    console.log(updateProjectResponse);

    if (Array.isArray(updateProjectResponse)) {
      const currentSpreadsheetRecordData: SpreadsheetRecordData[] =
        spreadsheetState ? [...spreadsheetState.results] : [];
      const newSpreadsheetRecordData = mappingErrorResponseToSpreadsheet(
        updateProjectResponse,
        currentSpreadsheetRecordData,
        InputComponentRequirementDialogInitialState.displayColumn,
      );

      setSpreadsheetState({
        ...spreadsheetState,
        results: newSpreadsheetRecordData,
      });
    } else {
      showToast({
        design: "completion",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00044),
      });
      closeModal(true);
    }
  };
  useEffect(onListenUpdateProjectResponse, [updateProjectResponse]);

  const getContent = () => {
    return (
      <>
        <div
          style={{
            paddingTop: "10px",
            paddingBottom: "30px",
            marginLeft: "30px",
          }}
        >
          <FrVrfFormPanel columns={1}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              <FrVrfRadioButtonField
                labelContent="DIP Test Environment Usage"
                style={{
                  "--vrf-radio-button-field-label-width": "290px",
                }}
                optionItems={pageState.codes.tstUseFlg}
                checked={pageState.edited.tstUseTypeCd}
                required
                constraint="IsNotBlank(value)"
                onRadioCheckedChanged={onChangeValueHandle("tstUseTypeCd")}
                errorMessage={errorMessages(
                  pageState.fieldPath.tstUseTypeCd,
                  isModalPage(),
                )}
              />
              <FrVrfButton
                icon="frIcon:question"
                theme="tertiary"
                size="small"
                tooltipVisible={true}
                tooltipPosition="below"
                tooltipContent={MANUAL_LINK.tooltip}
                style={{
                  "--vrf-tooltip-max-width": "120px",
                  marginRight: "50px",
                }}
                onClicked={() => {
                  window.open(MANUAL_LINK.value);
                }}
              />
            </div>
            <div>
              <div>
                <FrVrfRadioButtonField
                  labelContent="[Component Addition] or [Component Modification] or [New SharePoint Site Addition]"
                  style={{
                    "--vrf-radio-button-field-label-width": "290px",
                  }}
                  optionItems={pageState.codes.reqType}
                  checked={pageState.edited.reqType}
                  required
                  constraint="IsNotBlank(value)"
                  onRadioCheckedChanged={onChangeValueHandle("reqType")}
                  errorMessage={errorMessages(
                    pageState.fieldPath.reqType,
                    isModalPage(),
                  )}
                />
              </div>
              {pageState.edited.reqType === ReqType.Both ? (
                <div
                  style={{
                    paddingTop: "20px",
                  }}
                >
                  <FrVrfButtonPanel align="right">
                    <FrVrfButton
                      content="Add Row"
                      theme="secondary"
                      size="small"
                      onClicked={onClickAddRow}
                    />
                  </FrVrfButtonPanel>
                  <FrVrfSpreadsheet
                    data={spreadsheetInitialData}
                    templateUrl={TEMPLATE_FILE_URL}
                    onInitialized={initSpreadsheetState}
                    onCellEdited={onCellEditedHandle}
                    onCellClicked={onCellClickedHandle}
                    onCellSelected={onCellSelectedHandle}
                    onCellBulkEdited={onCellBulkEdited}
                    maxHeight={-70}
                  />
                </div>
              ) : null}
            </div>
          </FrVrfFormPanel>
          <FrVrfFooter>
            <FrVrfButton
              content="Cancel"
              theme="secondary"
              size="large"
              onClicked={onClickCancel}
            />
            <FrVrfButton
              content="Save"
              theme="primary"
              size="large"
              disabled={
                pageState.edited.reqType === ReqType.Both
                  ? pageState.status.invalidList.length !== 0 ||
                    pageState.status.invalidRecordList.length !== 0 ||
                    (JSON.stringify(pageState.saved) ===
                      JSON.stringify(pageState.edited) &&
                      !spreadsheetState?.results.some((e) => e.updateFlag))
                  : pageState.status.invalidList.length !== 0 ||
                    JSON.stringify(pageState.saved) ===
                      JSON.stringify(pageState.edited)
              }
              onClicked={onClickSave}
            />
          </FrVrfFooter>
        </div>
      </>
    );
  };

  return createModal(
    "IF and Component Requirement Definition",
    getContent(),
    onClickCancel,
    "large",
  );
};
